/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState, memo } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { LiveServerToolCall } from "@google/genai";
import { emailDeclaration } from "../tools-manager/ToolsManager";
import "./email-sender.scss";

interface EmailLog {
  timestamp: Date;
  to: string;
  subject: string;
  body: string;
  status: "sending" | "success" | "error";
  error?: string;
}

function EmailSenderComponent() {
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const { client } = useLiveAPIContext();

  useEffect(() => {
    const onToolCall = async (toolCall: LiveServerToolCall) => {
      if (!toolCall.functionCalls) {
        return;
      }
      const fc = toolCall.functionCalls.find(
        (fc) => fc.name === emailDeclaration.name
      );
      
      if (fc) {
        const { to, subject, body } = fc.args as any;
        
        // Only log the email - ToolsManager will actually send it
        const newLog: EmailLog = {
          timestamp: new Date(),
          to,
          subject,
          body,
          status: "sending",
        };
        setEmailLogs((prev) => [newLog, ...prev]);

        // Wait a bit then mark as success (ToolsManager handles actual sending)
        setTimeout(() => {
          setEmailLogs((prev) =>
            prev.map((log, idx) =>
              idx === 0 ? { ...log, status: "success" as const } : log
            )
          );
        }, 1000);
      }
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  // Component doesn't render UI - logs are in console
  return null;
}

// Simulated email sending function
// TODO: Replace with actual SMTP implementation
async function sendEmail(to: string, subject: string, body: string): Promise<void> {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  if (!backendUrl) {
    // Simulation mode - no backend configured
    await new Promise((resolve) => setTimeout(resolve, 1500));
    console.log("📧 Email would be sent (simulation mode):");
    console.log("To:", to);
    console.log("Subject:", subject);
    console.log("Body:", body);
    return;
  }

  // Real email sending via backend
  const response = await fetch(`${backendUrl}/api/send-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ to, subject, body }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send email');
  }

  const result = await response.json();
  console.log("✅ Email sent:", result.messageId);
}

export const EmailSender = memo(EmailSenderComponent);
